import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  cn,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpenCheck,
  CheckCircle2,
  Clock3,
  GraduationCap,
  Plus,
  ShieldCheck,
  X,
} from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import { listStaff } from "../setup/setup-api.js";
import {
  completeTrainingAssignment,
  createTrainingAssignments,
  createTrainingModule,
  listTrainingAssignments,
  listTrainingModules,
  signOffTrainingCompletion,
  trainingDisplayState,
  trainingStateLabel,
  type TrainingAssignment,
  type TrainingDisplayState,
} from "./training-api.js";

function stateTone(
  state: TrainingDisplayState,
): "attention" | "danger" | "neutral" | "positive" {
  return state === "complete"
    ? "positive"
    : state === "expired"
      ? "danger"
      : state === "expires_soon" || state === "awaiting_sign_off"
        ? "attention"
        : "neutral";
}

function ModuleEditor({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const id = useId();
  const create = useMutation({
    mutationFn: (form: FormData) =>
      createTrainingModule({
        content_md: String(form.get("content_md") ?? "").trim() || null,
        renewal_months: form.get("renewal_months")
          ? Number(form.get("renewal_months"))
          : null,
        title: String(form.get("title") ?? "").trim(),
      }),
    onSuccess: onSaved,
  });
  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-stone-950/45 p-4 backdrop-blur-sm">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="w-full max-w-xl rounded-3xl bg-surface p-6 shadow-2xl"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          create.mutate(new FormData(event.currentTarget));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Organisation module
            </p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              Create training module
            </h2>
          </div>
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor={`${id}-name`}>Module title</Label>
            <Input
              className="mt-1.5"
              id={`${id}-name`}
              name="title"
              placeholder="Safe allergen handover"
              required
            />
          </div>
          <div>
            <Label htmlFor={`${id}-content`}>Training content</Label>
            <textarea
              className="mt-1.5 min-h-36 w-full rounded-xl border border-stone-300 bg-surface p-3 text-sm"
              id={`${id}-content`}
              name="content_md"
              placeholder="What staff need to know and demonstrate…"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-renewal`}>Renew every (months)</Label>
            <Input
              className="mt-1.5"
              id={`${id}-renewal`}
              max="120"
              min="1"
              name="renewal_months"
              type="number"
            />
          </div>
        </div>
        {create.error ? (
          <p className="mt-4 text-sm font-semibold text-red-800">
            {displayError(create.error)}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={create.isPending} type="submit">
            {create.isPending ? "Creating…" : "Create module"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function AssignmentEditor({
  locationId,
  onClose,
  onSaved,
}: {
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const id = useId();
  const modules = useQuery({
    queryFn: listTrainingModules,
    queryKey: ["training-modules"],
  });
  const staff = useQuery({
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const assign = useMutation({
    mutationFn: (form: FormData) => {
      const profileIds = form.getAll("staff_profile_id").map(String);
      if (profileIds.length === 0)
        throw new Error("Choose at least one team member.");
      return createTrainingAssignments({
        due_on: String(form.get("due_on") ?? "") || null,
        location_id: locationId,
        staff_profile_ids: profileIds,
        training_module_id: String(form.get("training_module_id")),
      });
    },
    onSuccess: onSaved,
  });
  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-stone-950/45 p-4 backdrop-blur-sm">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="w-full max-w-xl rounded-3xl bg-surface p-6 shadow-2xl"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          assign.mutate(new FormData(event.currentTarget));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Bulk assignment
            </p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              Assign training
            </h2>
          </div>
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor={`${id}-module`}>Module</Label>
            <select
              className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
              id={`${id}-module`}
              name="training_module_id"
              required
            >
              <option value="">Choose a module</option>
              {modules.data?.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Team members</Label>
            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-stone-200 bg-surface p-2">
              {staff.data?.map((person) => (
                <label
                  className="flex cursor-pointer items-center gap-3 rounded-lg p-2.5 hover:bg-stone-50"
                  key={person.id}
                >
                  <input
                    className="size-4 accent-emerald-800"
                    name="staff_profile_id"
                    type="checkbox"
                    value={person.id}
                  />
                  <span>
                    <span className="block text-sm font-bold">
                      {person.display_name}
                    </span>
                    <span className="text-xs text-stone-500">
                      {person.job_title ?? "Team member"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor={`${id}-due`}>Due on</Label>
            <Input
              className="mt-1.5"
              id={`${id}-due`}
              name="due_on"
              type="date"
            />
          </div>
        </div>
        {assign.error ? (
          <p className="mt-4 text-sm font-semibold text-red-800">
            {assign.error instanceof Error && !("status" in assign.error)
              ? assign.error.message
              : displayError(assign.error)}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={assign.isPending} type="submit">
            {assign.isPending ? "Assigning…" : "Assign module"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function AssignmentDetail({
  assignment,
  canManage,
  isOwn,
  onClose,
  onUpdated,
}: {
  assignment: TrainingAssignment;
  canManage: boolean;
  isOwn: boolean;
  onClose: () => void;
  onUpdated: (assignment: TrainingAssignment) => void;
}) {
  const [note, setNote] = useState("");
  const complete = useMutation({
    mutationFn: () => completeTrainingAssignment(assignment.id),
    onSuccess: onUpdated,
  });
  const signOff = useMutation({
    mutationFn: () =>
      signOffTrainingCompletion(assignment.completion!.id, note),
    onSuccess: onUpdated,
  });
  const state = trainingDisplayState(assignment);
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-4 backdrop-blur-sm">
      <section
        aria-label="Training assignment"
        aria-modal="true"
        className="mx-auto my-8 w-full max-w-2xl rounded-3xl bg-surface p-6 shadow-2xl"
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <Badge tone={stateTone(state)}>{trainingStateLabel(state)}</Badge>
            <h2 className="mt-3 text-xl font-black">
              {assignment.module.title}
            </h2>
            <p className="mt-2 text-sm text-stone-500">
              {assignment.due_on ? `Due ${assignment.due_on}` : "No due date"}
              {assignment.module.renewal_months
                ? ` · renews every ${assignment.module.renewal_months} months`
                : ""}
            </p>
          </div>
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        {assignment.module.content_md ? (
          <div className="mt-5 rounded-2xl border border-stone-200 bg-surface p-5 text-sm leading-6 whitespace-pre-wrap text-stone-700">
            {assignment.module.content_md}
          </div>
        ) : null}
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div
            className={cn(
              "rounded-2xl border p-4",
              assignment.completion?.trainee_acknowledged_at
                ? "border-emerald-200 bg-emerald-50"
                : "border-stone-200 bg-surface",
            )}
          >
            <BookOpenCheck className="size-5 text-emerald-800" />
            <p className="mt-3 text-sm font-black">Trainee acknowledgement</p>
            <p className="mt-1 text-xs leading-5 text-stone-500">
              {assignment.completion?.trainee_acknowledged_at
                ? `Acknowledged ${new Date(assignment.completion.trainee_acknowledged_at).toLocaleDateString("en-NZ")}`
                : "The trainee confirms they completed the module."}
            </p>
            {isOwn && !assignment.completion ? (
              <Button
                className="mt-4"
                disabled={complete.isPending}
                onClick={() => complete.mutate()}
                size="sm"
              >
                {complete.isPending ? "Saving…" : "Mark completed"}
              </Button>
            ) : null}
          </div>
          <div
            className={cn(
              "rounded-2xl border p-4",
              assignment.completion?.trainer_acknowledged_at
                ? "border-emerald-200 bg-emerald-50"
                : "border-stone-200 bg-surface",
            )}
          >
            <ShieldCheck className="size-5 text-emerald-800" />
            <p className="mt-3 text-sm font-black">Trainer sign-off</p>
            <p className="mt-1 text-xs leading-5 text-stone-500">
              {assignment.completion?.trainer_acknowledged_at
                ? `Signed off ${new Date(assignment.completion.trainer_acknowledged_at).toLocaleDateString("en-NZ")}`
                : assignment.completion
                  ? "Waiting for a manager or trainer to confirm competency."
                  : "Available after the trainee acknowledgement."}
            </p>
          </div>
        </div>
        {canManage &&
        assignment.completion &&
        !assignment.completion.trainer_acknowledged_at ? (
          <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <Label htmlFor="competency-note">Competency note</Label>
            <textarea
              className="mt-1.5 min-h-20 w-full rounded-xl border border-emerald-200 bg-surface p-3 text-sm"
              id="competency-note"
              onChange={(event) => setNote(event.target.value)}
              placeholder="What was observed or discussed…"
              value={note}
            />
            <div className="mt-3 text-right">
              <Button
                disabled={signOff.isPending}
                onClick={() => signOff.mutate()}
              >
                {signOff.isPending ? "Signing off…" : "Sign off competency"}{" "}
                <CheckCircle2 className="size-4" />
              </Button>
            </div>
          </div>
        ) : null}
        {assignment.completion?.expires_on ? (
          <p className="mt-4 flex items-center gap-2 text-xs font-semibold text-stone-500">
            <Clock3 className="size-3.5" /> Valid until{" "}
            {assignment.completion.expires_on}
          </p>
        ) : null}
        {complete.error || signOff.error ? (
          <p className="mt-4 text-sm font-semibold text-red-800">
            {displayError(complete.error ?? signOff.error)}
          </p>
        ) : null}
      </section>
    </div>
  );
}

export function TrainingWorkspacePage() {
  const shell = useShell();
  const queryClient = useQueryClient();
  const locationId = shell.activeLocation?.id;
  const canManage = shell.activeRole !== "staff";
  const [assigning, setAssigning] = useState(false);
  const [creatingModule, setCreatingModule] = useState(false);
  const [selected, setSelected] = useState<TrainingAssignment | null>(null);
  const modules = useQuery({
    queryFn: listTrainingModules,
    queryKey: ["training-modules"],
  });
  const assignments = useQuery({
    enabled: Boolean(locationId),
    queryFn: listTrainingAssignments,
    queryKey: ["training-assignments", shell.activeOrganisationId],
  });
  const staff = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listStaff(locationId!),
    queryKey: ["staff", locationId],
  });
  const visibleAssignments =
    assignments.data?.filter((item) => item.location_id === locationId) ?? [];

  return (
    <>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            People
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Training
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Assign the right food-safety knowledge, capture both
            acknowledgements, and stay ahead of expiry.
          </p>
        </div>
        {canManage && locationId ? (
          <div className="flex gap-2">
            <Button onClick={() => setCreatingModule(true)} variant="outline">
              <Plus className="size-4" /> Module
            </Button>
            <Button onClick={() => setAssigning(true)}>
              <GraduationCap className="size-4" /> Assign training
            </Button>
          </div>
        ) : null}
      </header>
      {!locationId ? (
        <EmptyState
          description="Choose a location to see its training matrix."
          title="Choose a location"
        />
      ) : assignments.isPending || modules.isPending ? (
        <LoadingState label="Loading training" />
      ) : assignments.error || modules.error ? (
        <ErrorState
          message={displayError(assignments.error ?? modules.error)}
          onAction={() => {
            void assignments.refetch();
            void modules.refetch();
          }}
          title="Training isn’t available"
        />
      ) : canManage ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="sticky left-0 z-[1] bg-stone-50 px-4 py-3 text-xs font-black text-stone-500">
                      Module
                    </th>
                    {staff.data?.map((person) => (
                      <th
                        className="min-w-36 px-3 py-3 text-xs font-black text-stone-500"
                        key={person.id}
                      >
                        {person.display_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.data?.map((module) => (
                    <tr
                      className="border-b border-stone-100 last:border-0"
                      key={module.id}
                    >
                      <th className="sticky left-0 z-[1] max-w-56 bg-surface px-4 py-4">
                        <p className="text-sm font-black">{module.title}</p>
                        <p className="mt-1 text-xs font-normal text-stone-400">
                          {module.org_id ? "Organisation" : "SafePlate"}
                          {module.renewal_months
                            ? ` · ${module.renewal_months} mo`
                            : ""}
                        </p>
                      </th>
                      {staff.data?.map((person) => {
                        const assignment = visibleAssignments.find(
                          (item) =>
                            item.training_module_id === module.id &&
                            item.staff_profile_id === person.id,
                        );
                        const state = assignment
                          ? trainingDisplayState(assignment)
                          : null;
                        return (
                          <td className="px-3 py-4" key={person.id}>
                            {assignment && state ? (
                              <button
                                onClick={() => setSelected(assignment)}
                                type="button"
                              >
                                <Badge tone={stateTone(state)}>
                                  {trainingStateLabel(state)}
                                </Badge>
                              </button>
                            ) : (
                              <span className="text-xs text-stone-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {modules.data?.length === 0 ? (
              <div className="p-5">
                <EmptyState
                  calm
                  description="Create an organisation module or publish a plan that supplies SafePlate modules."
                  title="No training modules"
                />
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : visibleAssignments.length === 0 ? (
        <EmptyState
          calm
          description="Assigned modules will appear here without blocking your kitchen work."
          title="No training assigned"
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {visibleAssignments.map((assignment) => {
            const state = trainingDisplayState(assignment);
            return (
              <button
                className="text-left"
                key={assignment.id}
                onClick={() => setSelected(assignment)}
                type="button"
              >
                <Card className="h-full transition hover:border-emerald-700">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <span className="grid size-10 place-items-center rounded-xl bg-emerald-100 text-emerald-900">
                        <GraduationCap className="size-4" />
                      </span>
                      <Badge tone={stateTone(state)}>
                        {trainingStateLabel(state)}
                      </Badge>
                    </div>
                    <h2 className="mt-4 text-base font-black">
                      {assignment.module.title}
                    </h2>
                    <p className="mt-2 text-xs text-stone-500">
                      {assignment.due_on
                        ? `Due ${assignment.due_on}`
                        : "No due date"}
                    </p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      )}
      {assigning && locationId ? (
        <AssignmentEditor
          locationId={locationId}
          onClose={() => setAssigning(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ["training-assignments"],
            });
            setAssigning(false);
          }}
        />
      ) : null}
      {creatingModule ? (
        <ModuleEditor
          onClose={() => setCreatingModule(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ["training-modules"],
            });
            setCreatingModule(false);
          }}
        />
      ) : null}
      {selected ? (
        <AssignmentDetail
          assignment={selected}
          canManage={canManage}
          isOwn={shell.ownStaffProfileIds.includes(selected.staff_profile_id)}
          onClose={() => setSelected(null)}
          onUpdated={async (updated) => {
            setSelected(updated);
            await queryClient.invalidateQueries({
              queryKey: ["training-assignments"],
            });
          }}
        />
      ) : null}
    </>
  );
}
